import { useCallback, useEffect, useRef, useState } from "react";
import { Search, X, ChevronUp, ChevronDown, Send, Lock, Reply, Copy, Plus, Trash2, Sparkles, ShieldCheck, ArrowLeft, Mail, Pencil, Bookmark } from "lucide-react";
import { CardsIcon } from "../components/CardsIcon";
import { CopyReadingButton } from "../components/CopyReadingButton";
import { DeleteReadingButton } from "../components/DeleteReadingButton";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { mApi, MEMORIA_TOKEN_KEY } from "../lib/memoria";
import { MemoryLockGlyph } from "../components/MemoryLockGlyph";
import { MarkdownText } from "../components/MarkdownText";
import { PinBoxes } from "../components/PinBoxes";
import ChatReading from "../components/ChatReading";
import { FiguraRiconosciuta, FiguraInvite } from "../components/FiguraRiconosciuta";
import { isNativeApp } from "../lib/push";

const getToken = () => sessionStorage.getItem(MEMORIA_TOKEN_KEY);
const mHeaders = () => ({ headers: { "X-Memoria-Token": getToken() } });

const DEFAULT_TOPICS = [
  { id: "amore", emoji: "❤️", label: "Amore" },
  { id: "lavoro", emoji: "💼", label: "Lavoro" },
  { id: "amicizia", emoji: "👥", label: "Amicizia" },
  { id: "famiglia", emoji: "🏠", label: "Famiglia" },
  { id: "personale", emoji: "✨", label: "Personale" },
];

const CAT_LABELS = { fatto: "Fatti dichiarati", preferenza: "Preferenze osservate", ipotesi: "Ipotesi provvisorie" };

const time = (ts) => new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
const dayLabel = (ts) => new Date(ts).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

export default function Memoria() {
  const [phase, setPhase] = useState("loading"); // loading|create|unlock|forgot|chat
  const [createStep, setCreateStep] = useState(1);
  const [firstPin, setFirstPin] = useState("");
  const [pinReset, setPinReset] = useState(0);
  const [pinError, setPinError] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const [customTopics, setCustomTopics] = useState([]);
  const [topicId, setTopicId] = useState("generale");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [quote, setQuote] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [sending, setSending] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [newTopic, setNewTopic] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [resultIdx, setResultIdx] = useState(0);
  const [highlightId, setHighlightId] = useState(null);
  const [memory, setMemory] = useState({ enabled: false, personalization: true, facts: [], ai_trial: false });
  const [diario, setDiario] = useState([]);
  const [diarioBusy, setDiarioBusy] = useState(false);
  const [editFactId, setEditFactId] = useState(null);
  const [editFactText, setEditFactText] = useState("");
  const [editFactCat, setEditFactCat] = useState("fatto");
  const [filo, setFilo] = useState(null); // null | "offer" | "loading" | { text } | "done"
  const [resumeOffer, setResumeOffer] = useState(null);
  const [riepilogo, setRiepilogo] = useState(null);
  const [riepilogoShown, setRiepilogoShown] = useState(false);
  const [segnalibri, setSegnalibri] = useState([]);
  const [editDiaryId, setEditDiaryId] = useState(null);
  const [editDiaryText, setEditDiaryText] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [persone, setPersone] = useState([]);
  const [editPersonaId, setEditPersonaId] = useState(null);
  const [editPersonaText, setEditPersonaText] = useState("");
  const [figuraPopup, setFiguraPopup] = useState(null);
  const [figuraInv, setFiguraInv] = useState(null);
  useEffect(() => {
    if (showTopics && phase === "chat") mApi.get("/memoria/persone").then(({ data }) => setPersone(data.persons || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTopics, phase]);
  const togglePersonaStabile = async (p) => {
    try {
      await mApi.put(`/memoria/persone/${p.id}`, { stabile: !p.stabile });
      setPersone((arr) => arr.map((x) => (x.id === p.id ? { ...x, stabile: !p.stabile, gia_interpellata: true } : x)));
    } catch { toast.error("Non sono riuscita ad aggiornare. Riprova."); }
  };
  const savePersona = async (id) => {
    const t = editPersonaText.trim();
    if (!t) return;
    try {
      await mApi.put(`/memoria/persone/${id}`, { etichetta: t });
      setPersone((arr) => arr.map((x) => (x.id === id ? { ...x, etichetta: t } : x)));
      setEditPersonaId(null);
    } catch { toast.error("Non sono riuscita a salvare. Riprova."); }
  };
  const removePersona = async (id) => {
    try {
      await mApi.delete(`/memoria/persone/${id}`);
      setPersone((arr) => arr.filter((x) => x.id !== id));
    } catch { toast.error("Non sono riuscita a eliminare. Riprova."); }
  };
  useEffect(() => {
    if (wizardOpen) setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 80);
  }, [wizardOpen]);
  const scrollRef = useRef(null);
  const topicRef = useRef("generale");
  topicRef.current = topicId;
  const [vv, setVv] = useState(() => ({ h: typeof window !== "undefined" ? window.innerHeight : 800, top: 0 }));
  const [safeTop, setSafeTop] = useState(0);

  useEffect(() => {
    const probe = document.createElement("div");
    probe.style.cssText = "position:fixed;top:0;padding-top:env(safe-area-inset-top);visibility:hidden;pointer-events:none";
    document.body.appendChild(probe);
    setSafeTop(parseFloat(getComputedStyle(probe).paddingTop) || 0);
    probe.remove();
  }, []);

  useEffect(() => {
    const v = window.visualViewport;
    if (!v) return;
    const upd = () => setVv({ h: v.height, top: v.offsetTop });
    v.addEventListener("resize", upd);
    v.addEventListener("scroll", upd);
    upd();
    return () => { v.removeEventListener("resize", upd); v.removeEventListener("scroll", upd); };
  }, []);

  useEffect(() => {
    api.get("/memoria/status")
      .then((r) => setPhase(r.data.has_pin ? "unlock" : "create"))
      .catch(() => setPhase("unlock"));
  }, []);

  useEffect(() => {
    if (phase !== "chat") return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [phase]);

  const allTopics = [...DEFAULT_TOPICS, ...customTopics];
  const topicLabel = (id) => id === "generale" ? null : (allTopics.find((t) => t.id === id) || {}).label;
  const topicEmoji = (id) => (allTopics.find((t) => t.id === id) || {}).emoji;

  const handleLocked = useCallback(() => {
    sessionStorage.removeItem(MEMORIA_TOKEN_KEY);
    setPhase("unlock");
    setMessages([]);
  }, []);

  const loadMessages = useCallback(async (t) => {
    try {
      const { data } = await api.get(`/memoria/messages?topic_id=${t}`, mHeaders());
      setMessages(data);
    } catch (e) {
      if (e?.response?.status === 403) handleLocked();
    }
  }, [handleLocked]);

  useEffect(() => {
    if (phase === "chat") loadMessages(topicId);
  }, [topicId, phase, loadMessages]);

  const verdictTried = useRef(false);
  useEffect(() => {
    if (phase !== "chat" || verdictTried.current) return;
    verdictTried.current = true;
    api.post("/verdetto-settimanale/genera", {}, mHeaders()).then((r) => {
      if (r.data?.created && r.data.message) setMessages((m) => [...m, r.data.message]);
    }).catch(() => {});
  }, [phase]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const enterChat = async (token) => {
    sessionStorage.setItem(MEMORIA_TOKEN_KEY, token);
    setPhase("chat");
    try {
      const [t, mem] = await Promise.all([
        api.get("/memoria/topics", mHeaders()),
        api.get("/memoria/memory", mHeaders()),
      ]);
      setCustomTopics(t.data);
      setMemory(mem.data);
    } catch { /* ignore */ }
    loadDiario();
    checkFilo();
    loadUltimoTema();
    loadRiepilogo();
    loadSegnalibri();
  };

  const handleCreatePin = async (pin) => {
    if (createStep === 1) {
      setFirstPin(pin); setCreateStep(2); setPinError(""); setPinReset((k) => k + 1);
      return;
    }
    if (pin !== firstPin) {
      setCreateStep(1); setFirstPin(""); setPinError("I due PIN non coincidono. Riprova."); setPinReset((k) => k + 1);
      return;
    }
    setPinBusy(true);
    try {
      const { data } = await api.post("/memoria/pin", { pin });
      await enterChat(data.token);
    } catch (e) {
      setPinError(fmtErr(e)); setCreateStep(1); setFirstPin(""); setPinReset((k) => k + 1);
    } finally { setPinBusy(false); }
  };

  const handleUnlock = async (pin) => {
    setPinBusy(true);
    try {
      const { data } = await api.post("/memoria/unlock", { pin });
      await enterChat(data.token);
    } catch (e) {
      setPinError(fmtErr(e)); setPinReset((k) => k + 1);
    } finally { setPinBusy(false); }
  };

  const sendForgot = async () => {
    setPinBusy(true);
    try {
      await api.post("/memoria/pin/forgot");
      setForgotSent(true);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally { setPinBusy(false); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const tid = topicId;
    const q = quote;
    setInput(""); setQuote(null); setActionMsg(null);
    setSending(true);
    try {
      const { data } = await api.post("/memoria/message", {
        topic_id: tid, text,
        quote: q ? { text: q.text, ts: q.ts } : null,
      }, mHeaders());
      if (topicRef.current === tid) setMessages((m) => [...m, data.user, { ...data.reply, suggest: data.suggest_reading }]);
      if (data.remembered) {
        setMemory((mm) => ({ ...mm, facts: [...mm.facts, data.remembered] }));
        toast.success("Arcana lo custodirà nella memoria");
      }
    } catch (e) {
      if (e?.response?.status === 403) { handleLocked(); return; }
      toast.error(fmtErr(e, "Arcana non è disponibile in questo momento. Riprova tra poco."));
    } finally { setSending(false); }
  };

  const switchTopic = (t) => {
    setTopicId(t); setShowTopics(false); setActionMsg(null); setQuote(null);
  };

  const addTopic = async () => {
    const label = newTopic.trim();
    if (!label) return;
    try {
      const { data } = await api.post("/memoria/topics", { label }, mHeaders());
      setCustomTopics((ts) => [...ts, data]);
      setNewTopic("");
      switchTopic(data.id);
    } catch (e) {
      if (e?.response?.status === 403) { handleLocked(); return; }
      toast.error(fmtErr(e));
    }
  };

  const toggleMemory = async () => {
    const next = !memory.enabled;
    setMemory((mm) => ({ ...mm, enabled: next }));
    try {
      await api.put("/memoria/memory", { enabled: next }, mHeaders());
    } catch (e) {
      setMemory((mm) => ({ ...mm, enabled: !next }));
      if (e?.response?.status === 403) handleLocked();
    }
  };

  const togglePersonalization = async () => {
    const next = !memory.personalization;
    setMemory((mm) => ({ ...mm, personalization: next }));
    try {
      await api.put("/memoria/memory", { personalization: next }, mHeaders());
    } catch (e) {
      setMemory((mm) => ({ ...mm, personalization: !next }));
      if (e?.response?.status === 403) handleLocked();
    }
  };

  const saveFact = async (id, cat) => {
    const text = editFactText.trim();
    try {
      const { data } = await api.put(`/memoria/memory/${id}`, { text, cat }, mHeaders());
      setMemory((mm) => ({ ...mm, facts: mm.facts.map((f) => (f.id === id ? data.fact : f)) }));
      setEditFactId(null);
      toast.success("Memoria aggiornata.");
    } catch (e) {
      if (e?.response?.status === 403) { handleLocked(); return; }
      toast.error(fmtErr(e));
    }
  };

  const removeFact = async (id) => {
    setMemory((mm) => ({ ...mm, facts: mm.facts.filter((f) => f.id !== id) }));
    try { await api.delete(`/memoria/memory/${id}`, mHeaders()); } catch { /* ignore */ }
  };

  const loadDiario = useCallback(async () => {
    try {
      const { data } = await api.get("/memoria/diario", mHeaders());
      setDiario(data.entries || []);
      setMemory((mm) => ({ ...mm, ai_trial: !!data.ai_trial }));
    } catch { /* ignore */ }
  }, []);

  const generaDiario = async () => {
    setDiarioBusy(true);
    try {
      const { data } = await api.post("/memoria/diario/genera", {}, mHeaders());
      setDiario((d) => [data.entry, ...d]);
      toast.success("Diario aggiornato.");
    } catch (e) {
      if (e?.response?.status === 403) { handleLocked(); return; }
      toast.error(fmtErr(e));
    } finally { setDiarioBusy(false); }
  };

  const removeDiarioEntry = async (id) => {
    setDiario((d) => d.filter((e) => e.id !== id));
    try { await api.delete(`/memoria/diario/${id}`, mHeaders()); } catch { /* ignore */ }
  };

  const loadUltimoTema = async () => {
    if (sessionStorage.getItem("arcana_resume_dismissed")) return;
    try {
      const { data } = await api.get("/memoria/ultimo-tema", mHeaders());
      if (data.topic_id && data.topic_id !== topicRef.current) setResumeOffer(data);
    } catch { /* nessun collegamento chiaro: non mostrare nulla */ }
  };

  const riprendiUltimoTema = () => {
    if (resumeOffer) setTopicId(resumeOffer.topic_id);
    setResumeOffer(null);
  };

  const ignoraUltimoTema = () => {
    sessionStorage.setItem("arcana_resume_dismissed", "1");
    setResumeOffer(null);
  };

  const loadRiepilogo = async () => {
    try {
      const { data } = await api.get("/memoria/riepilogo", mHeaders());
      if (data.available) setRiepilogo(data);
    } catch { /* non mostrare nulla */ }
  };

  const chiudiRiepilogo = async () => {
    const month = riepilogo?.month;
    setRiepilogo(null);
    setRiepilogoShown(false);
    if (month) { try { await api.post("/memoria/riepilogo/chiudi", { month }, mHeaders()); } catch { /* ignore */ } }
  };

  const loadSegnalibri = async () => {
    try {
      const { data } = await api.get("/memoria/segnalibri", mHeaders());
      setSegnalibri(data.items || []);
    } catch { /* ignore */ }
  };

  const toggleSegnalibro = async (id) => {
    try {
      const { data } = await api.put(`/memoria/segnalibri/${id}`, {}, mHeaders());
      setMessages((msgs) => msgs.map((x) => (x.id === id ? { ...x, bookmarked: data.bookmarked } : x)));
      setSegnalibri((s) => data.bookmarked
        ? [messages.find((x) => x.id === id), ...s].filter(Boolean)
        : s.filter((x) => x.id !== id));
      toast.success(data.bookmarked ? "Lettura aggiunta ai segnalibri." : "Lettura rimossa dai segnalibri.");
    } catch (e) {
      if (e?.response?.status === 403) { handleLocked(); return; }
      toast.error("Non riesco ad aggiornare il segnalibro. Riprova.");
    }
  };

  const saveDiaryEntry = async (id) => {
    const text = editDiaryText.trim();
    if (!text) return;
    try {
      await api.put(`/memoria/diario/${id}`, { text }, mHeaders());
      setDiario((d) => d.map((e) => (e.id === id ? { ...e, text } : e)));
      setEditDiaryId(null);
      toast.success("Pagina aggiornata.");
    } catch (e) {
      if (e?.response?.status === 403) { handleLocked(); return; }
      toast.error(fmtErr(e));
    }
  };

  const checkFilo = useCallback(async () => {
    try {
      const { data } = await api.post("/filo/offer");
      if (data.offer) setFilo("offer");
    } catch { /* non insistere mai */ }
  }, []);

  const accettaFilo = async () => {
    setFilo("loading");
    try {
      const { data } = await api.post("/filo/sintesi");
      setFilo({ text: data.text });
    } catch (e) {
      setFilo(null);
      toast.error(fmtErr(e, "La sintesi non è disponibile in questo momento."));
    }
  };

  const declinaFilo = async () => {
    setFilo(null);
    try { await api.post("/filo/declina"); } catch { /* non insistere mai */ }
  };

  const deleteMessage = async (id) => {
    try {
      await mApi.delete(`/memoria/messages/${id}`);
      setMessages((msgs) => msgs.filter((x) => x.id !== id));
      setActionMsg(null);
      setConfirmDeleteId(null);
      toast.success("Messaggio eliminato.");
    } catch (e) {
      if (e?.response?.status === 403) { handleLocked(); return; }
      toast.error(fmtErr(e));
    }
  };

  const wipeConversation = async () => {
    try {
      await mApi.delete("/memoria/messages");
      setMessages([]);
      setConfirmWipe(false);
      toast.success("Conversazione eliminata.");
    } catch (e) {
      if (e?.response?.status === 403) { handleLocked(); return; }
      toast.error(fmtErr(e));
    }
  };

  useEffect(() => {
    if (!searchOpen) return;
    const t = setTimeout(() => {
      const q = query.trim();
      if (q.length < 2) { setResults([]); return; }
      api.get(`/memoria/search?q=${encodeURIComponent(q)}`, mHeaders())
        .then((r) => { setResults(r.data); setResultIdx(0); })
        .catch((e) => { if (e?.response?.status === 403) handleLocked(); });
    }, 300);
    return () => clearTimeout(t);
  }, [query, searchOpen, handleLocked]);

  const jumpTo = (idx) => {
    const r = results[idx];
    if (!r) return;
    setResultIdx(idx);
    const doScroll = () => {
      document.getElementById(`msg-${r.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(r.id);
      setTimeout(() => setHighlightId(null), 2600);
    };
    doScroll();
  };

  const copyMsg = (m) => {
    navigator.clipboard?.writeText(m.text);
    toast.success("Messaggio copiato");
    setActionMsg(null);
  };

  const pinScreen = (children) => (
    <div className="max-w-md mx-auto px-4 py-14 text-center" data-testid="pin-lock-screen">
      <div className="w-16 h-16 mx-auto rounded-full border border-amber-500/40 bg-[#1a0b2b] flex items-center justify-center shadow-[0_0_35px_rgba(251,191,36,0.12)]">
        <MemoryLockGlyph className="w-9 h-9" />
      </div>
      <h1 className="font-display text-3xl text-amber-100 mt-6">La mia memoria 🔒</h1>
      {children}
    </div>
  );

  if (phase === "loading") {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="font-cinzel text-amber-400 tracking-[0.3em] text-sm animate-pulse">ARCANA</div></div>;
  }

  if (phase === "create") {
    return pinScreen(
      <div data-testid="pin-create-flow">
        <p className="text-purple-100/80 text-sm leading-relaxed mt-5 max-w-sm mx-auto">
          Crea un PIN per proteggere la tua memoria. Senza il PIN nessuno può accedere alle tue conversazioni.
          Le tue conversazioni rimarranno qui, così potrai ritrovarle nel tempo.
        </p>
        <p className="text-amber-200/90 text-xs tracking-[0.2em] uppercase mt-8 mb-4" data-testid="pin-step-label">
          {createStep === 1 ? "Scegli un PIN di 4 cifre" : "Ripeti il PIN per confermare"}
        </p>
        <PinBoxes testid="pin-create" onComplete={handleCreatePin} resetKey={pinReset} />
        {pinError && <p className="text-rose-300 text-xs mt-4" data-testid="pin-error">{pinError}</p>}
        <p className="text-purple-300/50 text-[11px] mt-8">Il PIN protegge solo questa area privata, non il resto dell'app.</p>
      </div>
    );
  }

  if (phase === "unlock") {
    return pinScreen(
      <div data-testid="pin-unlock-flow">
        <p className="text-purple-100/80 text-sm mt-5">Inserisci il tuo PIN per aprire la tua memoria.</p>
        <div className="mt-8">
          <PinBoxes testid="pin-unlock" onComplete={handleUnlock} resetKey={pinReset} />
        </div>
        {pinError && <p className="text-rose-300 text-xs mt-4" data-testid="pin-error">{pinError}</p>}
        {pinBusy && <p className="text-purple-300/50 text-xs mt-3">Verifica in corso…</p>}
        <button
          data-testid="pin-forgot-btn"
          onClick={() => { setPhase("forgot"); setForgotSent(false); setPinError(""); }}
          className="text-amber-300/80 text-xs underline underline-offset-4 decoration-amber-500/40 mt-8 hover:text-amber-200"
        >
          PIN dimenticato?
        </button>
      </div>
    );
  }

  if (phase === "forgot") {
    return pinScreen(
      <div data-testid="pin-recovery-flow">
        {!forgotSent ? (
          <>
            <p className="text-purple-100/80 text-sm leading-relaxed mt-5 max-w-sm mx-auto">
              Ti invieremo un'email al tuo indirizzo di account con un <strong className="text-amber-100">link sicuro</strong> per creare un nuovo PIN.
              Il PIN non viene mai mostrato né inviato via email.
            </p>
            <button data-testid="pin-recovery-send" onClick={sendForgot} disabled={pinBusy} className="gold-btn mt-6 px-8 py-3 text-sm disabled:opacity-50">
              {pinBusy ? "Invio in corso…" : "Invia link di recupero"}
            </button>
            <button
              data-testid="pin-recovery-back"
              onClick={() => setPhase("unlock")}
              className="flex items-center gap-1.5 mx-auto mt-5 text-purple-300/70 text-xs hover:text-purple-200"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Torna al PIN
            </button>
          </>
        ) : (
          <div className="max-w-sm mx-auto">
            <ShieldCheck className="w-10 h-10 text-amber-300 mx-auto mt-4" />
            <p className="text-purple-100/80 text-sm leading-relaxed mt-5" data-testid="pin-recovery-sent">
              Ti abbiamo inviato un'email con un link valido 30 minuti. Aprendolo potrai creare un nuovo PIN.
              <strong className="text-amber-100"> Le tue conversazioni restano al loro posto.</strong>
            </p>
            <button
              data-testid="pin-recovery-back-2"
              onClick={() => setPhase("unlock")}
              className="flex items-center gap-1.5 mx-auto mt-6 text-purple-300/70 text-xs hover:text-purple-200"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Torna al PIN
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="memoria-page"
      className="fixed left-0 right-0 z-40 flex flex-col bg-[#0d0416]"
      style={{ top: 57 + safeTop + vv.top, height: Math.max(vv.h - 57 - safeTop, 320) }}
    >
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 45% at 75% 0%, rgba(138,35,135,0.28), transparent 70%), radial-gradient(ellipse 50% 40% at 15% 18%, rgba(88,28,135,0.22), transparent 70%)" }} />
      <div className="relative max-w-2xl w-full mx-auto px-4 pt-3 pb-3 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between gap-2 border-b border-[#4a2175]/60 pb-3">
          <button
            data-testid="memory-settings-btn"
            onClick={() => setShowTopics(true)}
            title="Memoria di Arcana"
            className="flex items-center gap-2 text-purple-200/80 hover:text-amber-200 transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
            <span className="text-xs">Memoria</span>
          </button>
          <div className="text-center">
            <p className="font-display text-xl text-amber-100 leading-none inline-flex items-center gap-2" data-testid="chat-title"><MemoryLockGlyph className="w-4 h-4" /> Arcana</p>
            <p className="text-[11px] text-purple-300/60 mt-1">La tua conversazione privata</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="search-toggle-btn"
              onClick={() => { setSearchOpen((s) => !s); setQuery(""); setResults([]); }}
              className={`p-2 rounded-full transition-colors ${searchOpen ? "text-amber-300 bg-amber-500/10" : "text-purple-200/80 hover:text-amber-200"}`}
            >
              <Search className="w-[18px] h-[18px]" />
            </button>
            <button
              data-testid="lock-btn"
              onClick={handleLocked}
              title="Blocca la memoria"
              className="p-2 rounded-full text-purple-200/80 hover:text-amber-200 transition-colors"
            >
              <Lock className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div data-testid="search-bar" className="mt-3 rounded-xl border border-[#4a2175] bg-[#150920] p-3">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-amber-300/70 shrink-0" />
              <input
                data-testid="search-input"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca nelle conversazioni…"
                className="flex-1 bg-transparent text-sm text-purple-100 placeholder-purple-300/40 focus:outline-none"
              />
              {results.length > 0 && (
                <span className="text-[11px] text-amber-200/80 whitespace-nowrap" data-testid="search-count">
                  {resultIdx + 1}/{results.length} risultati
                </span>
              )}
              {query.trim().length >= 2 && results.length === 0 && (
                <span className="text-[11px] text-purple-300/60 whitespace-nowrap" data-testid="search-count">0 risultati</span>
              )}
              <button data-testid="search-prev" disabled={!results.length} onClick={() => jumpTo((resultIdx - 1 + results.length) % results.length)} className="p-1.5 text-purple-200/80 hover:text-amber-200 disabled:opacity-30">
                <ChevronUp className="w-4 h-4" />
              </button>
              <button data-testid="search-next" disabled={!results.length} onClick={() => jumpTo((resultIdx + 1) % results.length)} className="p-1.5 text-purple-200/80 hover:text-amber-200 disabled:opacity-30">
                <ChevronDown className="w-4 h-4" />
              </button>
              <button data-testid="search-close" onClick={() => { setSearchOpen(false); setQuery(""); setResults([]); }} className="p-1.5 text-purple-200/80 hover:text-amber-200">
                <X className="w-4 h-4" />
              </button>
            </div>
            {results.length > 0 && (
              <div className="mt-2 max-h-44 overflow-y-auto divide-y divide-[#4a2175]/40" data-testid="search-results">
                {results.map((r, i) => (
                  <button
                    key={r.id}
                    data-testid={`search-result-${i}`}
                    onClick={() => jumpTo(i)}
                    className={`w-full text-left px-2 py-2 text-xs rounded transition-colors ${i === resultIdx ? "bg-amber-500/10" : "hover:bg-white/5"}`}
                  >
                    <span className="text-amber-300/80">{r.role === "user" ? "Tu" : "Arcana"}</span>
                    <span className="text-purple-300/50"> · {dayLabel(r.ts)}</span>
                    <span className="block text-purple-100/80 truncate mt-0.5">{r.text}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto py-4 space-y-3 overscroll-contain" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center pt-10" data-testid="chat-empty">
              <Sparkles className="w-6 h-6 text-amber-400/60 mx-auto mb-3" />
              <p className="text-purple-200/60 text-sm max-w-xs mx-auto leading-relaxed">
                Questo è il tuo spazio privato. Scrivi ad Arcana liberamente: quello che vi dite resta qui, protetto dal tuo PIN.
              </p>
            </div>
          )}
          {messages.map((m, i) => {
            const showDay = i === 0 || dayLabel(messages[i - 1].ts) !== dayLabel(m.ts);
            return (
              <div key={m.id}>
                {showDay && (
                  <div className="flex items-center gap-3 my-4">
                    <span className="h-px flex-1 bg-[#4a2175]/50" />
                    <span className="text-[10px] uppercase tracking-widest text-purple-300/50">{dayLabel(m.ts)}</span>
                    <span className="h-px flex-1 bg-[#4a2175]/50" />
                  </div>
                )}
                <div id={`msg-${m.id}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} ${m.suggest ? "flex-col items-start" : ""}`}>
                  {m.kind === "verdict" ? (
                    <div className="max-w-[92%] w-full chrome-card px-4 py-4 shadow-[0_0_30px_rgba(251,191,36,0.12)]" data-testid="verdict-message">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/90 font-medium">✦ Verdetto Settimanale</p>
                      {m.verdict?.cards_summary && <p className="text-amber-200/70 text-[11px] mt-1.5 leading-relaxed">{m.verdict.cards_summary}</p>}
                      <div className="text-purple-100/90 text-sm leading-relaxed mt-3 pt-3 border-t border-[#4a2175]/50">
                        <MarkdownText text={m.text} />
                      </div>
                      <div className="mt-3 flex justify-end gap-2 flex-wrap">
                        <CopyReadingButton text={m.text} testid={`copy-verdict-${m.id}`} />
                        <DeleteReadingButton testid={`delete-verdict-${m.id}`} onDelete={async () => {
                          await mApi.delete(`/memoria/messages/${m.id}`);
                          setMessages((msgs) => msgs.filter((x) => x.id !== m.id));
                          toast.success("Testo eliminato.");
                        }} />
                      </div>
                      <span className="block text-[10px] mt-2 text-purple-300/40">{time(m.ts)}</span>
                    </div>
                  ) : m.kind === "reading" ? (
                    <div className="max-w-[92%] w-full chrome-card px-4 py-4 shadow-[0_0_30px_rgba(251,191,36,0.08)]" data-testid="reading-message">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/90 font-medium">✨ Lettura · {m.reading?.method}</p>
                      {m.reading?.question && <p className="text-purple-200/70 text-xs italic mt-1.5">“{m.reading.question}”</p>}
                      {m.reading?.cards_summary && <p className="text-amber-200/70 text-[11px] mt-2 leading-relaxed">{m.reading.cards_summary}</p>}
                      <div className="text-purple-100/90 text-sm leading-relaxed mt-3 pt-3 border-t border-[#4a2175]/50">
                        <MarkdownText text={m.text} />
                      </div>
                      <div className="mt-3 flex justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => toggleSegnalibro(m.id)}
                          data-testid={`bookmark-${m.id}`}
                          aria-label={m.bookmarked ? "Rimuovi dai segnalibri" : "Aggiungi ai segnalibri"}
                          className="text-[10px] uppercase tracking-wider border border-[#4a2175] rounded-full px-3 py-1.5 text-purple-200/70 hover:border-amber-500/50 transition-colors inline-flex items-center gap-1.5"
                        >
                          <Bookmark className={`w-3.5 h-3.5 ${m.bookmarked ? "fill-amber-300 text-amber-300" : ""}`} />
                          {m.bookmarked ? "Nei segnalibri" : "Segnalibro"}
                        </button>
                        <CopyReadingButton text={m.text} testid={`copy-reading-${m.id}`} />
                        <DeleteReadingButton testid={`delete-reading-${m.id}`} onDelete={async () => {
                          await mApi.delete(`/memoria/messages/${m.id}`);
                          setMessages((msgs) => msgs.filter((x) => x.id !== m.id));
                          toast.success("Testo eliminato.");
                        }} />
                      </div>
                      <span className="block text-[10px] mt-2 text-purple-300/40">{time(m.ts)}</span>
                    </div>
                  ) : (
                  <>
                  <button
                    data-testid={`message-${m.role}`}
                    onClick={() => setActionMsg(actionMsg?.id === m.id ? null : m)}
                    className={`max-w-[82%] text-left rounded-2xl px-4 py-2.5 text-sm leading-relaxed transition-all duration-500 ${
                      m.role === "user"
                        ? "bg-gradient-to-r from-purple-900/60 to-fuchsia-950/60 border border-purple-500/30 text-purple-50 rounded-br-md"
                        : "bg-[#1c092b]/90 border border-[#d4af37]/30 text-purple-100 rounded-bl-md"
                    } ${highlightId === m.id ? "ring-2 ring-amber-400 shadow-[0_0_25px_rgba(251,191,36,0.35)]" : ""} ${actionMsg?.id === m.id ? "ring-1 ring-amber-400/60" : ""}`}
                  >
                    {m.quote && (
                      <span className="block mb-2 pl-2.5 border-l-2 border-amber-400/70 text-[11px] text-purple-200/70 italic" data-testid="quote-block">
                        <span className="not-italic text-amber-300/80 font-medium">
                          Messaggio ripreso · {dayLabel(m.quote.ts)}
                        </span>
                        <span className="block mt-0.5">“{m.quote.text.length > 90 ? m.quote.text.slice(0, 90) + "…" : m.quote.text}”</span>
                      </span>
                    )}
                    <MarkdownText text={m.text} />
                    <span className={`block text-[10px] mt-1 ${m.role === "user" ? "text-amber-200/40 text-right" : "text-purple-300/40"}`}>{time(m.ts)}</span>
                  </button>
                  {m.suggest && (
                    <button
                      data-testid="start-reading-btn"
                      onClick={() => setWizardOpen(true)}
                      className="gold-btn mt-2 px-5 py-2 text-xs"
                    >
                      ✨ Inizia una lettura
                    </button>
                  )}
                  </>
                  )}
                </div>
              </div>
            );
          })}
          {resumeOffer && (
            <div className="flex justify-start" data-testid="resume-offer">
              <div className="max-w-[92%] w-full chrome-card px-4 py-4 border border-amber-500/30">
                <p className="font-cinzel text-[11px] tracking-[0.25em] uppercase text-amber-400/80 mb-2">Continua da dove eravamo</p>
                <p className="text-sm text-purple-100/85 leading-relaxed">L'ultima volta stavamo parlando di «{resumeOffer.label}». Vuoi riprendere da lì?</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={riprendiUltimoTema} data-testid="resume-accept" className="gold-btn px-4 py-2 text-xs">Riprendi</button>
                  <button onClick={ignoraUltimoTema} data-testid="resume-dismiss" className="text-xs text-purple-300/70 hover:text-purple-200 px-3 py-2">Qualcosa di nuovo</button>
                </div>
              </div>
            </div>
          )}
          {riepilogo && !riepilogoShown && (
            <div className="flex justify-start" data-testid="riepilogo-offer">
              <div className="max-w-[92%] w-full chrome-card px-4 py-4 border border-amber-500/30">
                <p className="font-cinzel text-[11px] tracking-[0.25em] uppercase text-amber-400/80 mb-2">Il filo del mese</p>
                <p className="text-sm text-purple-100/85 leading-relaxed">Vuoi un breve riepilogo di ciò che mi hai raccontato questo mese?</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setRiepilogoShown(true)} data-testid="riepilogo-show" className="gold-btn px-4 py-2 text-xs">Mostra</button>
                  <button onClick={chiudiRiepilogo} data-testid="riepilogo-dismiss" className="text-xs text-purple-300/70 hover:text-purple-200 px-3 py-2">Non ora</button>
                </div>
              </div>
            </div>
          )}
          {riepilogo && riepilogoShown && (
            <div className="flex justify-start" data-testid="riepilogo-view">
              <div className="max-w-[92%] w-full chrome-card px-4 py-4 border border-amber-500/30">
                <p className="font-cinzel text-[11px] tracking-[0.25em] uppercase text-amber-400/80 mb-2">Il filo del mese</p>
                <p className="text-xs text-purple-200/70 mb-2">Questo mese mi hai raccontato:</p>
                <ul className="space-y-1.5">
                  {riepilogo.facts.map((f) => (
                    <li key={f.id} className="text-sm text-purple-100/85 leading-relaxed">· {f.text}</li>
                  ))}
                </ul>
                {riepilogo.letture_mese > 0 && (
                  <p className="text-xs text-purple-200/60 mt-3">Hai consultato le carte {riepilogo.letture_mese} {riepilogo.letture_mese === 1 ? "volta" : "volte"}.</p>
                )}
                <p className="text-[10px] text-purple-300/40 mt-3 italic">Solo ciò che mi hai raccontato tu: niente previsioni, niente interpretazioni nuove.</p>
                <button onClick={chiudiRiepilogo} data-testid="riepilogo-close" className="text-[11px] text-purple-300/60 hover:text-purple-200 mt-2">Chiudi</button>
              </div>
            </div>
          )}
          {filo && (
            <div className="flex justify-start" data-testid="filo-memoria-card">
              <div className="max-w-[92%] w-full chrome-card px-4 py-4 border border-amber-500/30">
                <p className="font-cinzel text-[11px] tracking-[0.25em] uppercase text-amber-400/80 mb-2">Il filo delle tue consultazioni</p>
                {filo === "offer" && (
                  <>
                    <p className="text-sm text-purple-100/85 leading-relaxed">Vuoi che ti riassuma il filo delle ultime consultazioni?</p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={accettaFilo} data-testid="filo-memoria-accept" className="gold-btn px-4 py-2 text-xs">Sì, riassumilo</button>
                      <button onClick={declinaFilo} data-testid="filo-memoria-decline" className="text-xs text-purple-300/70 hover:text-purple-200 px-3 py-2">No, grazie</button>
                    </div>
                  </>
                )}
                {filo === "loading" && <p className="text-sm text-purple-200/70 italic" data-testid="filo-memoria-loading">Rileggo le tue stese…</p>}
                {filo?.text && (
                  <>
                    <p className="text-sm text-purple-100/85 leading-relaxed whitespace-pre-wrap" data-testid="filo-memoria-text">{filo.text}</p>
                    <button onClick={() => setFilo(null)} data-testid="filo-memoria-close" className="text-[11px] text-purple-300/60 hover:text-purple-200 mt-2">Chiudi</button>
                  </>
                )}
              </div>
            </div>
          )}
          {sending && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div className="bg-[#1c092b]/90 border border-[#d4af37]/30 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                {[0, 1, 2].map((d) => (
                  <span key={d} className="w-1.5 h-1.5 rounded-full bg-amber-300/70 animate-bounce" style={{ animationDelay: `${d * 0.18}s` }} />
                ))}
              </div>
            </div>
          )}
          {wizardOpen && (
            <div data-testid="memoria-reading-wizard" className="rounded-2xl border border-amber-500/30 bg-[#12081e]/95 shadow-[0_0_30px_rgba(212,175,55,0.08)]">
              <ChatReading
                topicId={topicId}
                onDone={(msg) => {
                  setMessages((m) => [...m, msg]);
                  setWizardOpen(false);
                  if (msg?.figura) setFiguraInv({ figura: msg.figura, suggerita: msg.persona_suggerita || null, readingId: msg.reading_ref?.id || "" });
                }}
                onClose={() => setWizardOpen(false)}
              />
            </div>
          )}
        </div>

        {actionMsg && (
          <div data-testid="message-actions" className="flex items-center gap-2 mt-2 rounded-xl border border-[#4a2175] bg-[#150920] px-3 py-2">
            <button
              data-testid="forward-btn"
              onClick={() => {
                setQuote({ text: actionMsg.text, ts: actionMsg.ts, from: actionMsg.role });
                setActionMsg(null);
              }}
              className="flex items-center gap-1.5 text-xs text-amber-200 hover:text-amber-100 px-2 py-1.5 rounded-lg hover:bg-amber-500/10"
            >
              <Reply className="w-3.5 h-3.5" /> Riprendi in conversazione
            </button>
            <button data-testid="copy-btn" onClick={() => copyMsg(actionMsg)} className="flex items-center gap-1.5 text-xs text-purple-200/80 hover:text-purple-100 px-2 py-1.5 rounded-lg hover:bg-white/5">
              <Copy className="w-3.5 h-3.5" /> Copia
            </button>
            {confirmDeleteId === actionMsg.id ? (
              <button data-testid="delete-msg-confirm" onClick={() => deleteMessage(actionMsg.id)} className="flex items-center gap-1.5 text-xs text-red-300 bg-red-500/10 border border-red-400/50 px-2 py-1.5 rounded-lg">
                <Trash2 className="w-3.5 h-3.5" /> Confermi?
              </button>
            ) : (
              <button data-testid="delete-msg-btn" onClick={() => { setConfirmDeleteId(actionMsg.id); setTimeout(() => setConfirmDeleteId(null), 4000); }} className="flex items-center gap-1.5 text-xs text-red-300/70 hover:text-red-300 px-2 py-1.5 rounded-lg hover:bg-red-500/10">
                <Trash2 className="w-3.5 h-3.5" /> Elimina
              </button>
            )}
            <button data-testid="actions-close" onClick={() => setActionMsg(null)} className="ml-auto p-1 text-purple-300/60 hover:text-purple-100">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {figuraInv && !figuraPopup && (
          <FiguraInvite onOpen={() => setFiguraPopup(figuraInv)} onDismiss={() => setFiguraInv(null)} />
        )}
        {quote && (
          <div data-testid="quote-preview" className="mt-2 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2">
            <Reply className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
            <p className="flex-1 text-[11px] text-purple-100/70 italic line-clamp-2">“{quote.text}”</p>
            <button data-testid="quote-remove" onClick={() => setQuote(null)} className="p-0.5 text-purple-300/60 hover:text-purple-100">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <div className="mt-2 flex items-end gap-2">
          <button
            data-testid="new-reading-btn"
            onClick={() => setWizardOpen(true)}
            title="Nuova lettura con le carte"
            className="w-11 h-11 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 flex items-center justify-center shrink-0 transition-colors"
          >
            <CardsIcon className="w-5 h-5" />
          </button>
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={quote ? "Questo era quello che ti avevo detto…" : "Scrivi ad Arcana…"}
            rows={1}
            className="flex-1 resize-none rounded-2xl border border-[#4a2175] bg-[#150920] px-4 py-3 text-sm text-purple-100 placeholder-purple-300/40 focus:outline-none focus:border-amber-400/60 transition-colors"
          />
          <button
            data-testid="chat-send-btn"
            onClick={send}
            disabled={!input.trim() || sending}
            className="gold-btn w-11 h-11 rounded-full flex items-center justify-center disabled:opacity-40 shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-center text-[10px] text-purple-300/40 mt-2">
          Parlare con Arcana qui non consuma crediti. Le letture restano nella loro sezione.
        </p>
      </div>

      {figuraPopup && (
        <FiguraRiconosciuta
          figura={figuraPopup.figura}
          suggerita={figuraPopup.suggerita}
          readingId={figuraPopup.readingId}
          onClose={() => { setFiguraPopup(null); setFiguraInv(null); }}
        />
      )}
      {showTopics && (
        <div className="fixed left-0 right-0 bottom-0 z-50 bg-black/60 backdrop-blur-sm" style={{ top: 57 + safeTop + vv.top }} onClick={() => setShowTopics(false)}>
          <div
            data-testid="memory-settings-drawer"
            className="drawer-in-left absolute left-0 top-0 bottom-0 w-[86vw] max-w-80 bg-[#150920] border-r border-[#4a2175] p-5 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch", paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-lg text-amber-100">Memoria di Arcana</h3>
              <button data-testid="memory-settings-close" onClick={() => setShowTopics(false)} className="p-1 text-purple-300/70 hover:text-purple-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[11px] text-purple-300/60 mb-4 leading-relaxed">
              Una sola conversazione continua con Arcana: tutto ciò che vi dite resta qui, in un unico spazio protetto dal tuo PIN.
            </p>
            <label className="flex items-center justify-between gap-3 text-sm text-purple-100/80">
              <span>Permetti ad Arcana di ricordare</span>
              <button
                data-testid="memory-toggle"
                onClick={toggleMemory}
                className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${memory.enabled ? "bg-amber-500/80" : "bg-[#4a2175]"}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${memory.enabled ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </label>
            <p className="text-[11px] text-purple-300/50 mt-2 leading-relaxed">
              La memoria non modifica mai le letture: quelle restano basate sulle carte e sul metodo.
            </p>
            <label className="flex items-center justify-between gap-3 text-sm text-purple-100/80 mt-4">
              <span>Personalizzazione (tono e stile)</span>
              <button
                data-testid="personalization-toggle"
                onClick={togglePersonalization}
                className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${memory.personalization ? "bg-amber-500/80" : "bg-[#4a2175]"}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${memory.personalization ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </label>
            <p className="text-[11px] text-purple-300/50 mt-2 leading-relaxed">
              Adatta solo il modo in cui Arcana comunica con te: non cambia mai carte, significati o contenuti delle letture.
            </p>
            {memory.facts.length > 0 && (
              <div className="mt-4 space-y-3" data-testid="memory-facts">
                {["fatto", "preferenza", "ipotesi"].map((cat) => {
                  const items = memory.facts.filter((f) => (f.cat || "fatto") === cat);
                  if (!items.length) return null;
                  return (
                    <div key={cat} data-testid={`memory-cat-${cat}`}>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 mb-1.5">{CAT_LABELS[cat]}</p>
                      {cat === "ipotesi" && (
                        <p className="text-[10px] text-purple-300/40 italic mb-1.5">Non sono mai certezze: Arcana le ignora se le carte non le confermano.</p>
                      )}
                      <div className="space-y-2">
                        {items.map((f) => (
                          <div key={f.id} className="rounded-lg border border-[#4a2175]/70 bg-[#1a0b2b] px-2.5 py-2">
                            {editFactId === f.id ? (
                              <div data-testid={`memory-edit-${f.id}`}>
                                <textarea value={editFactText} onChange={(e) => setEditFactText(e.target.value)} rows={2}
                                  className="w-full bg-[#150920] border border-[#4a2175] rounded-lg px-2 py-1.5 text-[11px] text-purple-100" />
                                <select value={editFactCat} onChange={(e) => setEditFactCat(e.target.value)} data-testid={`memory-cat-select-${f.id}`}
                                  className="mt-1.5 w-full bg-[#150920] border border-[#4a2175] rounded-lg px-2 py-1.5 text-[11px] text-purple-100">
                                  <option value="fatto">Fatto dichiarato</option>
                                  <option value="preferenza">Preferenza osservata</option>
                                  <option value="ipotesi">Ipotesi provvisoria</option>
                                </select>
                                <div className="flex gap-2 mt-1.5">
                                  <button onClick={() => saveFact(f.id, editFactCat)} data-testid={`memory-save-${f.id}`} className="gold-btn px-3 py-1 text-[10px]">Salva</button>
                                  <button onClick={() => setEditFactId(null)} data-testid={`memory-cancel-${f.id}`} className="text-[10px] text-purple-300/70 px-2">Annulla</button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2">
                                <p className="flex-1 text-[11px] text-purple-100/80 leading-snug">{f.text}</p>
                                <button data-testid={`memory-edit-btn-${f.id}`} onClick={() => { setEditFactId(f.id); setEditFactText(f.text); setEditFactCat(f.cat || "fatto"); }} className="p-0.5 text-purple-300/50 hover:text-amber-300 shrink-0">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button data-testid={`memory-delete-${f.id}`} onClick={() => removeFact(f.id)} className="p-0.5 text-purple-300/50 hover:text-rose-300 shrink-0">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-5 pt-4 border-t border-[#4a2175]/50" data-testid="segnalibri-section">
              <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 mb-2">Segnalibri</p>
              <p className="text-[11px] text-purple-300/50 leading-relaxed mb-3">
                Le letture che hai contrassegnato: puoi rileggerle o confrontarle con la tua situazione attuale. Le letture originali restano intatte.
              </p>
              {segnalibri.length > 0 ? (
                <div className="space-y-2" data-testid="segnalibri-list">
                  {segnalibri.map((s) => (
                    <div key={s.id} className="rounded-lg border border-[#4a2175]/70 bg-[#1a0b2b] px-2.5 py-2" data-testid={`segnalibro-${s.id}`}>
                      <div className="flex items-start gap-2">
                        <button onClick={() => { setTopicId(s.topic_id); setShowTopics(false); }} data-testid={`segnalibro-open-${s.id}`} className="flex-1 text-left">
                          <p className="text-[11px] text-amber-200/80">{s.reading?.method}</p>
                          {s.reading?.question && <p className="text-[10px] text-purple-200/60 italic mt-0.5">“{s.reading.question}”</p>}
                          <p className="text-[10px] text-purple-300/40 mt-0.5">{dayLabel(s.ts)}</p>
                        </button>
                        <button data-testid={`segnalibro-remove-${s.id}`} onClick={() => toggleSegnalibro(s.id)} aria-label="Rimuovi dai segnalibri" className="p-0.5 text-purple-300/50 hover:text-rose-300 shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-purple-300/40 italic" data-testid="segnalibri-empty">Nessuna lettura nei segnalibri: usa l'icona a forma di segnalibro su una lettura nella chat.</p>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-[#4a2175]/50" data-testid="persone-section">
              <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 mb-2">Persone riconosciute</p>
              <p className="text-[11px] text-purple-300/50 leading-relaxed mb-3">
                Le figure che hai identificato nelle stese. Arcana non le nomina mai da sola: le propone solo come possibilità quando il quadro lo sostiene.
              </p>
              {persone.length > 0 ? (
                <div className="space-y-2" data-testid="persone-list">
                  {persone.map((p) => (
                    <div key={p.id} className="rounded-lg border border-[#4a2175]/70 bg-[#1a0b2b] px-2.5 py-2" data-testid={`persona-${p.id}`}>
                      {editPersonaId === p.id ? (
                        <div>
                          <input value={editPersonaText} onChange={(e) => setEditPersonaText(e.target.value)} maxLength={120}
                            data-testid={`persona-edit-input-${p.id}`}
                            className="w-full bg-[#150920] border border-[#4a2175] rounded-lg px-2 py-1.5 text-[11px] text-purple-100" />
                          <div className="flex gap-2 mt-1.5">
                            <button onClick={() => savePersona(p.id)} data-testid={`persona-save-${p.id}`} className="gold-btn px-3 py-1 text-[10px]">Salva</button>
                            <button onClick={() => setEditPersonaId(null)} className="text-[10px] text-purple-300/70 px-2">Annulla</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <p className="text-[11px] text-purple-100/80 leading-snug">{p.etichetta}</p>
                            <p className="text-[10px] text-purple-300/40 mt-0.5">{p.ruolo || "figura emersa nelle stese"}</p>
                          </div>
                          <button onClick={() => togglePersonaStabile(p)} data-testid={`persona-stabile-${p.id}`}
                            className={`text-[9px] px-2 py-0.5 rounded-full border shrink-0 transition-colors ${p.stabile ? "border-amber-500/50 text-amber-300" : "border-[#4a2175] text-purple-300/50"}`}>
                            {p.stabile ? "ricordata" : "occasionale"}
                          </button>
                          <button data-testid={`persona-edit-btn-${p.id}`} onClick={() => { setEditPersonaId(p.id); setEditPersonaText(p.etichetta); }} className="p-0.5 text-purple-300/50 hover:text-amber-300 shrink-0">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`persona-delete-${p.id}`} onClick={() => removePersona(p.id)} className="p-0.5 text-purple-300/50 hover:text-rose-300 shrink-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-purple-300/40 italic" data-testid="persone-empty">Nessuna persona identificata: quando una figura emerge chiaramente da una stesa, Arcana te lo chiederà.</p>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-[#4a2175]/50" data-testid="diario-section">
              <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 mb-2">Diario silenzioso</p>
              <p className="text-[11px] text-purple-300/50 leading-relaxed mb-3">
                Una sintesi privata delle tue situazioni, delle questioni aperte e dei loro sviluppi nel tempo. Resta qui, protetta dal PIN.
              </p>
              {memory.ai_trial && (
                <button data-testid="diario-genera-btn" onClick={generaDiario} disabled={diarioBusy} className="gold-btn px-4 py-2 text-xs mb-3 disabled:opacity-50">
                  {diarioBusy ? "Aggiorno…" : "Aggiorna il diario (max 1 volta al giorno)"}
                </button>
              )}
              {diario.length > 0 ? (
                <div className="space-y-2" data-testid="diario-entries">
                  {diario.map((e) => (
                    <div key={e.id} className="rounded-lg border border-[#4a2175]/70 bg-[#1a0b2b] px-2.5 py-2">
                      {editDiaryId === e.id ? (
                        <div data-testid={`diario-edit-${e.id}`}>
                          <textarea value={editDiaryText} onChange={(ev) => setEditDiaryText(ev.target.value)} rows={3}
                            className="w-full bg-[#150920] border border-[#4a2175] rounded-lg px-2 py-1.5 text-[11px] text-purple-100" />
                          <div className="flex gap-2 mt-1.5">
                            <button onClick={() => saveDiaryEntry(e.id)} data-testid={`diario-save-${e.id}`} className="gold-btn px-3 py-1 text-[10px]">Salva</button>
                            <button onClick={() => setEditDiaryId(null)} data-testid={`diario-cancel-${e.id}`} className="text-[10px] text-purple-300/70 px-2">Annulla</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start gap-2">
                            <p className="flex-1 text-[10px] uppercase tracking-wider text-amber-300/60">{dayLabel(e.ts)}</p>
                            <button data-testid={`diario-edit-btn-${e.id}`} onClick={() => { setEditDiaryId(e.id); setEditDiaryText(e.text); }} aria-label="Correggi questa pagina" className="p-0.5 text-purple-300/50 hover:text-amber-300 shrink-0">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button data-testid={`diario-delete-${e.id}`} onClick={() => removeDiarioEntry(e.id)} className="p-0.5 text-purple-300/50 hover:text-rose-300 shrink-0">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-[11px] text-purple-100/80 leading-snug mt-1 whitespace-pre-wrap">{e.text}</p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-purple-300/40 italic" data-testid="diario-empty">Nessuna pagina di diario ancora.</p>
              )}
            </div>
            {isNativeApp() && (
              <div className="mt-5 pt-4 border-t border-[#4a2175]/50" data-testid="verdetto-push-info">
                <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 mb-2">Notifica del Verdetto</p>
                <p className="text-[11px] text-purple-300/60 leading-relaxed">
                  Quando il nuovo Verdetto Settimanale è pronto ricevi una notifica sul telefono: contiene solo l'avviso,
                  mai il testo della lettura. Il suono si sceglie dalle impostazioni Android del telefono
                  (Impostazioni → App → Arcana → Notifiche → Verdetto Settimanale → Suono): la tua scelta resta salvata
                  e l'app non la modifica mai.
                </p>
              </div>
            )}
            <div className="mt-6 pt-4 border-t border-red-500/20">
              {!confirmWipe ? (
                <button data-testid="wipe-conversation-btn" onClick={() => setConfirmWipe(true)} className="text-[11px] text-red-300/80 hover:text-red-300 border border-red-500/40 rounded-full px-4 py-2 transition-colors">
                  Elimina tutta la conversazione
                </button>
              ) : (
                <div data-testid="wipe-conversation-confirm">
                  <p className="text-[11px] text-purple-200/80 leading-relaxed mb-3">
                    Eliminare definitivamente tutta la conversazione con Arcana, incluse le letture fatte qui? L'operazione non si può annullare.
                  </p>
                  <div className="flex gap-2">
                    <button data-testid="wipe-conversation-yes" onClick={wipeConversation} className="text-[11px] bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/50 rounded-full px-4 py-2 transition-colors">
                      Sì, elimina tutto
                    </button>
                    <button data-testid="wipe-conversation-no" onClick={() => setConfirmWipe(false)} className="text-[11px] text-purple-300/70 hover:text-purple-200 px-3 py-2">
                      Annulla
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
